import type { PropsWithChildren } from 'react'
import React, { Component } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Clickable from '../Clickable'
import Expander from 'components/utils/Expander'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'

const Root = styled.div`
  & + & {
    margin-top: ${rem(12)};
  }
`

const CollapseBody = styled.div<{ noPadding?: boolean }>`
  padding: ${({ noPadding }) => noPadding ? 0 : `${rem(20)} 0 ${rem(40)} ${rem(32)}`};
`

const ClickableLabel = styled(Clickable)`
  display: flex;
  width: 100%;
  align-items: center;

  > .label {
    flex: 1;
  }
`

interface Props {
  open?: boolean
  label?: React.ReactNode
  noPadding?: boolean
  toggleTestId?: string
  toggleClassName?: string
  id?: string
  onExpand?: () => void
  onToggle?: (open: boolean) => void
  onCollapse?: () => void
}

interface State {
  open: boolean
}

class CollapsibleGroup extends Component<PropsWithChildren<Props>, State> {
  constructor(props: PropsWithChildren<Props>) {
    super(props)
    this.state = { open: !!props.open }

    this.toggleCollapse = this.toggleCollapse.bind(this)
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.open !== this.props.open) {
      this.setState({ open: !!this.props.open })
    }
  }

  expand() {
    this.setState({ open: true }, () => {
      this.props.onExpand?.()
      this.props.onToggle?.(true)
    })
  }

  collapse() {
    this.setState({ open: false }, () => {
      this.props.onCollapse?.()
      this.props.onToggle?.(false)
    })
  }

  toggleCollapse() {
    this.state.open ? this.collapse() : this.expand()
  }

  render() {
    const {
      children,
      label = null,
      noPadding,
      toggleClassName,
      id,
      toggleTestId,
    } = this.props

    const { open } = this.state

    return (
      <Root id={id}>
        <ClickableLabel
          data-testid={toggleTestId}
          className={toggleClassName}
          onClick={this.toggleCollapse}
        >
          <div className="label">{label}</div>
          <DropdownChevron open={open} />
        </ClickableLabel>
        <Expander expanded={open}>
          <CollapseBody noPadding={noPadding}>
            {children}
          </CollapseBody>
        </Expander>
      </Root>
    )
  }
}

export default CollapsibleGroup
